import React, { useState, useEffect } from "react";
import { Card, ListGroup } from "react-bootstrap";
import { collection, getDocs, query, where, orderBy, Timestamp, limit } from 'firebase/firestore';
import { db } from '../../../firebase/FirebaseProvider';
import { Event, User } from "../../types/Types";
import { useNavigate } from "react-router";
import EventCard from "../../events/EventCard";
import { logDataLoaded } from "../../util/Util";

interface Props {
  socialDisabled: boolean;
  currentUser: User;
}

function UpcomingEventsWidget(props: Props) {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Array<Event>>([]);
  const [totalEventCount, setTotalEventCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    loadUpcomingEvents();
  }, []);

  const loadUpcomingEvents = async () => {
    setLoading(true);
    try {
      // First, get total count of incomplete events
      const countQuery = query(
        collection(db, 'events'),
        where('eventCompleted', '==', false),
      );
      const countSnap = await getDocs(countQuery);
      setTotalEventCount(countSnap.size);
      
      // Then get the first 3 incomplete events to display (ordered by scheduled date)
      const eventsCollection = query(
        collection(db, 'events'),
        where('eventCompleted', '==', false),
        orderBy('scheduledAt', 'asc'),
        limit(3),
      );
      const eventsSnap = await getDocs(eventsCollection);
      const eventsList: Array<Event> = [];
      eventsSnap.forEach((doc) => {
        if (eventsList.length < 3) {
          const eventData = doc.data();
          eventsList.push({
            id: doc.id,
            userId: eventData.userId,
            createdAt: eventData.createdAt?.toDate(),
            scheduledAt: eventData.scheduledAt?.toDate(),
            eventTitle: eventData.eventTitle,
            eventType: eventData.eventType,
            eventDescription: eventData.eventDescription,
            locationId: eventData.locationId,
          } as Event);
        }
      });
      logDataLoaded('upcoming events', eventsList.length);
      setEvents(eventsList);
    } catch (error) {
      console.error('Error loading upcoming events:', error);
    }
    setLoading(false);
  };

  if (props.socialDisabled) {
    return null;
  }

  if (loading) {
    return (
      <Card className="mb-3">
        <Card.Header>
          <div><b>Upcoming Events</b></div>
        </Card.Header>
        <Card.Body style={{ padding: "0.75rem" }}>
          <div className={'loading'}>Loading...</div>
        </Card.Body>
      </Card>
    );
  }

  // Always show the widget, even if no events
  return (
    <Card className="mb-3">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <b>Upcoming Events</b>
          <small>
            <a 
              href="/events" 
              onClick={(e) => { e.preventDefault(); navigate('/events'); }}
              style={{ textDecoration: 'none', cursor: 'pointer' }}
            >
              View all
            </a>
          </small>
        </div>
      </Card.Header>
      <Card.Body style={{ padding: '0.75rem' }}>
        {events.length === 0 ? (
          <div className="text-muted">
            <small>No upcoming events scheduled</small>
          </div>
        ) : (
          <>
            <ListGroup variant="flush" style={{ paddingBottom: '0' }}>
              {events.map((event, index) => (
                <EventCard 
                  key={event.id}
                  event={event}
                  onClick={() => navigate('/events')}
                  showDescription={false}
                  isLastItem={index === events.length - 1 && totalEventCount <= 3}
                  clickable={true}
                />
              ))}
            </ListGroup>
            {totalEventCount > 3 && (
              <div 
                onClick={() => navigate('/events')}
                style={{ 
                  padding: '0.75rem',
                  fontSize: '0.85rem',
                  color: 'lightgray',
                  backgroundColor: '#444',
                  borderRadius: '0.25rem',
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
              >
                Plus {totalEventCount - 3} more event{totalEventCount - 3 !== 1 ? 's' : ''} coming soon...
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default UpcomingEventsWidget;
